import { useMemo } from 'react';
import type { BrandResponse, StoreResponse } from '@clarity/contract';
import { useBrands, useStores } from '../queries/index.js';
import { useClientContext } from '../lib/clientContext.js';
import { useSession } from '../lib/session.js';
import { useScopeContextOptional } from '../lib/scopeContext.js';
import { useI18n } from '../lib/i18n.js';
import { QueryState } from './QueryState.js';
import { BarChart, Donut, Legend } from '../components/Chart.js';
import { IconDashboard } from '../components/icons.js';
import { num } from './format.js';
import { OnboardingNote, splitOnboarded } from './combined/shared.js';
import { DashboardBalance } from './DashboardBalance.js';
import { DashboardOperations } from './DashboardOperations.js';
import { MythingsManualEntries } from './MythingsManualEntries.js';
import { StoreAnalyticsEntry } from './StoreAnalyticsEntry.js';

/**
 * The dashboard roll-up: a status glance over the IN-SCOPE branches — store
 * lifecycle counts, onboarding progress, and a per-platform split — rendered with
 * the glass charts so data-viz lives in the same design system. All scoping is
 * client-side over the typed query client; the server still enforces tenant
 * isolation.
 *
 * Scope-aware (clarity-navigation-scope task 6.3, Req 6.1 / 6.2 / 5.6). The roll-up
 * aggregates over the branches the ACTIVE SCOPE covers rather than always the
 * whole client:
 *
 *   - ALL scope   → every client branch (the historical behaviour),
 *   - BRAND scope → only that brand's branches,
 *   - STORE scope → the one branch.
 *
 * It reads the scope via {@link useScopeContextOptional}, which returns `null`
 * OUTSIDE a `<ScopeProvider>`. The Dashboard also renders on the legacy
 * `/c/:clientId/dashboard` route (kept until task 7) which is NOT wrapped in a
 * provider; there the hook returns `null` and the Dashboard falls back to the
 * original whole-client narrowing (admin narrows by `selectedClient`; a
 * client-role user trusts the server's tenant scoping).
 */
export function Dashboard() {
  const { selectedClient } = useClientContext();
  const { role } = useSession();
  const { localeTag, t } = useI18n();
  const scopeCtx = useScopeContextOptional();
  const brands = useBrands();
  const stores = useStores();

  // The in-scope branch set + the brands those branches belong to. Inside the
  // scope tree this is the scope's resolved branch set (all | brand's | the one
  // store); on the legacy route (no provider) it falls back to the whole-client
  // narrowing the dashboard has always done.
  const scoped = useMemo(() => {
    const brandRows: BrandResponse[] = brands.data?.status === 200 ? brands.data.body : [];
    const storeRows: StoreResponse[] = stores.data?.status === 200 ? stores.data.body : [];

    if (scopeCtx) {
      // In the scope tree: aggregate over exactly the in-scope branches, and the
      // brands those branches belong to (for the brand count / platform split).
      const branches = scopeCtx.branches;
      const inScopeBrandIds = new Set(branches.map((s) => s.brandId));
      const scopeBrands =
        scopeCtx.scope.kind === 'brand' && scopeCtx.brand
          ? [scopeCtx.brand]
          : scopeCtx.brandGroups
              .map((g) => g.brand)
              .filter((b) => inScopeBrandIds.has(b.id));
      return { clientBrands: scopeBrands, branches };
    }

    // Legacy route (no scope provider): the original whole-client narrowing — an
    // admin inside one client narrows to that client's brands/branches; a client
    // trusts the server's tenant scoping (its lists already contain only its own).
    const narrow = role === 'admin' && selectedClient !== null;
    const clientBrands = narrow
      ? brandRows.filter((b) => b.clientId === selectedClient.id)
      : brandRows;
    const clientBrandIds = new Set(clientBrands.map((b) => b.id));
    const branches = narrow
      ? storeRows.filter((s) => clientBrandIds.has(s.brandId))
      : storeRows;
    return { clientBrands, branches };
  }, [brands.data, stores.data, role, selectedClient, scopeCtx]);

  const { clientBrands, branches } = scoped;

  const counts = useMemo(() => {
    const byState: Record<StoreResponse['state'], number> = {
      assigned: 0,
      enabled: 0,
      disabled: 0,
      removed: 0,
    };
    let onboarded = 0;
    for (const s of branches) {
      byState[s.state] += 1;
      if (s.onboardingComplete) onboarded += 1;
    }
    return { byState, onboarded };
  }, [branches]);

  // How many in-scope branches are not yet onboarded (Req 5.6 / 6.2) — surfaced
  // as the shared "N still onboarding" note so the dashboard reads consistently
  // with the combined Orders/Statements/Payouts views.
  const stillOnboarding = useMemo(
    () => splitOnboarded(branches).stillOnboarding,
    [branches],
  );

  const platformSplit = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of clientBrands) m.set(b.platformKey, (m.get(b.platformKey) ?? 0) + 1);
    const colors = ['#14b8b3', '#38bdf8', '#5fe3df', '#0d8f90'];
    return [...m.entries()].map(([label, value], i) => ({
      label,
      value,
      color: colors[i % colors.length] ?? '#14b8b3',
    }));
  }, [clientBrands]);

  const isLoading = brands.isLoading || stores.isLoading;
  const isError = brands.isError || stores.isError;
  const total = branches.length;

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>Dashboard</h2>
          <p className="sub">
            {selectedClient ? `${selectedClient.name} · ` : ''}
            Store health and reconciliation at a glance.
          </p>
        </div>
      </div>

      <QueryState isLoading={isLoading} isError={isError} label="overview">
        {total === 0 ? (
          <div className="card empty">
            <IconDashboard className="empty__glyph" />
            <p>No branches yet. Import a brand under Brands to populate this dashboard.</p>
          </div>
        ) : (
          <div className="dash">
            {/*
             * Summary band — ONE panel, not three equal stat cards. The branch
             * count leads (it frames everything below); "Enabled & live" and
             * "Onboarded" sit as a tighter supporting pair divided by a hairline.
             * Proximity does the grouping, so the eye lands on the lead number
             * first instead of scanning a flat triptych of identical tiles.
             */}
            {/*
             * The summary band now LEADS with the estimated balance — the
             * owner's headline "money still coming" figure (clarity-daily-update
             * Req 4/5) — because that is the number they open the dashboard for.
             * Branches / Enabled / Onboarded become the supporting trio. The
             * balance is scope-aware (one branch's figure, or the summed figure
             * across the in-scope branches). `summary-band--bal` widens the lead
             * slot for the figure + breakdown.
             */}
            <section className="card summary-band summary-band--bal" aria-label="Store summary">
              <DashboardBalance branches={branches} />
              <div className="summary-band__rest summary-band__rest--trio">
                <div className="stat">
                  <span className="stat__label">Branches</span>
                  <span className="stat__value">{num(total, localeTag)}</span>
                  <span className="stat__sub">
                    across {num(clientBrands.length, localeTag)} brand
                    {clientBrands.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat__label">Enabled &amp; live</span>
                  <span className="stat__value">{num(counts.byState.enabled, localeTag)}</span>
                  <span className="stat__sub">ingesting daily</span>
                </div>
                <div className="stat">
                  <span className="stat__label">Onboarded</span>
                  <span className="stat__value">
                    {num(counts.onboarded, localeTag)}
                    <span className="unit">/ {num(total, localeTag)}</span>
                  </span>
                  <span className="stat__sub">ready to enable</span>
                </div>
              </div>
            </section>

            {/*
             * The in-scope onboarding note (Req 5.6 / 6.2): how many in-scope
             * branches are still onboarding and thus contribute no data. Shared
             * with the combined views so the copy + i18n keys stay consistent.
             */}
            {stillOnboarding > 0 && <OnboardingNote count={stillOnboarding} t={t} />}

            {/*
             * Operations glance (clarity-owner-analytics): ratings, delivery time
             * and the new-vs-returning split across the in-scope branches, beneath
             * the money band. Each branch's reads fan out into hidden sources.
             */}
            <DashboardOperations branches={branches} />

            {/*
             * Single-branch scope: let the owner record this branch's operational
             * metrics (rating / delivery / conversion) that Clarity can't pull.
             */}
            {branches.length === 1 && branches[0] && branches[0].onboardingComplete && (
              <StoreAnalyticsEntry store={branches[0]} />
            )}

            {/*
             * MyThings owner-entered payouts + expenses (clarity-balance-
             * coverage Req 3, 4): MyThings has no statement, so the owner records
             * what it paid them + any costs, and the balance estimate reconciles.
             * One panel per onboarded MyThings branch.
             */}
            {branches
              .filter((b) => b.platformKey === 'mythings' && b.onboardingComplete)
              .map((b) => (
                <MythingsManualEntries key={b.id} store={b} />
              ))}

            {/* Analysis row — the two charts as equivalent peers. */}
            <div className="dash-analysis">
              <section className="card">
                <h3>Store lifecycle</h3>
                <BarChart
                  ariaLabel="Branch count by lifecycle state"
                  height={200}
                  format={(n) => num(n, localeTag)}
                  data={(['assigned', 'enabled', 'disabled', 'removed'] as const).map((st) => ({
                    label: st,
                    value: counts.byState[st],
                  }))}
                />
              </section>

              <section className="card dash-platform">
                <h3>Brands by platform</h3>
                <div className="dash-platform__body">
                  <Donut
                    ariaLabel="Brands by platform"
                    segments={platformSplit}
                    centerLabel={num(clientBrands.length, localeTag)}
                    centerSub="brands"
                  />
                  <Legend
                    items={platformSplit.map((s) => ({
                      label: s.label,
                      color: s.color,
                      value: num(s.value, localeTag),
                    }))}
                  />
                </div>
              </section>
            </div>
          </div>
        )}
      </QueryState>
    </section>
  );
}
